import { AiConsentState } from '../attestations/ai-consent.types';

// Mirrors flow_ai.df serializers.  Decimal fields arrive as strings.

import { BadgeVariant, StatusMeta } from '../status/status-meta.types';
import { DocAssessStatus } from '../status/doc-assess.types';
import { KsRecoveryFlag } from '../ops/ops-recovery.types';
import { SizingHold } from '../applications/sizing-hold.types';

export type { KsRecoveryFlag };

export type { BadgeVariant, StatusMeta };

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export type DfStatus = 'NONE' | 'IN_PROGRESS' | 'ACTIVE';

type DfApplicationStage = 'DRAFT' | 'SUBMITTED' | 'MORE_INFO_REQUIRED' | 'DECLINED';

export type DfFacilitySubtype = 'FACTORING' | 'SID' | 'HYBRID';

export type DfFacilityDocType =
  'BANK_STATEMENT' | 'FINANCIALS' | 'AGE_ANALYSIS' | 'ID_DOCUMENT' | 'OTHER';

/** A facility-level supporting document, with its per-upload AI verdict. */
export interface DfFacilityDocument {
  short_id: string;
  doc_type: DfFacilityDocType;
  label: string;
  file_name: string;
  content_type: string;
  created_at: string;
  assess_status: DocAssessStatus;
  assess_message: string;
}

type InvoiceStatus =
  | 'SUBMITTED'
  | 'PENDING_REVIEW'
  | 'NEEDS_DOCS'
  | 'ON_HOLD'
  | 'APPROVED_FOR_FUNDING'
  | 'FUNDED'
  | 'OVERDUE'
  | 'PARTIALLY_PAID'
  | 'SOLD_BACK'
  | 'SETTLED'
  | 'DECLINED'
  | 'CANCELLED';

type ApprovalStatus =
  'PENDING' | 'IN_PROGRESS' | 'APPROVED' | 'LIMITED' | 'EXCLUDED' | 'PAUSED' | 'TERMINATED';

// ---------------------------------------------------------------------------
// Sub-objects
// ---------------------------------------------------------------------------

export interface AiRecommendation {
  recommendation: string;
  summary: string;
  confidence: number;
  risk_flags?: string[];
  missing_items?: string[];
}

/**
 * One model's result within a comparison ("bake-off") run. Ops-only — lets the
 * reviewer see how each model voted and what it cost, side by side.
 */
interface AiPerModelResult {
  model: string;
  provider: string;
  recommendation: string | null;
  /** Doc-check verdict (OK/WARNING/PROBLEM); null for full assessments. */
  verdict: string | null;
  confidence: number | null;
  summary: string;
  /** Money as strings to preserve decimal precision. */
  cost_usd: string;
  cost_zar: string;
  latency_ms: number;
  /** The pre-assessment area this call answered (`§A11.7` U7); '' for a one-call run. */
  area?: string;
}

/**
 * Full persisted advisory from the most recent completed assessment (Ops-only).
 * Unlike `latest_ai_recommendation` (the enum mirror), this carries the rationale
 * the reviewer needs to justify a decision, plus — when more than one model was
 * run — the per-model comparison and agreement signal. `null` when no assessment
 * has run.
 */
/**
 * The most recent assessment ATTEMPT, whether or not it produced a verdict.
 *
 * Distinct from {@link LatestAiAdvisory}, which only exists once an assessment
 * SUCCEEDS. Since the Ops assess endpoint returns 202 (FLOW-211), a page waiting
 * for a result has to tell "still running" from "died" — and a failed run is
 * absent from the advisory either way, so absence alone means nothing.
 */
export interface LatestAiAttempt {
  status: 'RUNNING' | 'COMPLETE' | 'FAILED';
  started_at: string | null;
  /** Internal diagnostic. Ops-only (N4) — never shown to a client. */
  error: string;
  /** What this attempt ran on and cost. A refused answer was still billed. */
  model?: string;
  cost_zar?: string;
  cost_usd?: string;
  latency_ms?: number;
  /** Every call of this attempt as its log row records it. Platform admins only. */
  log?: AiAttemptLogLine[];
}

/** One call of an attempt, off its `AiRequestLog` row. */
export interface AiAttemptLogLine {
  ref: string;
  area: string;
  model: string;
  provider: string;
  status: string;
  stop_reason: string;
  error: string;
  started_at: string;
  latency_ms: number;
  input_tokens: number;
  output_tokens: number;
  request_s3_key: string;
}

export interface LatestAiAdvisory {
  recommendation: string | null;
  confidence: number | null;
  summary: string;
  risk_flags: string[];
  missing_items: string[];
  assessed_at: string | null;
  /** A canned answer stored with the AI switched off, not a model's reading. */
  is_stub?: boolean;

  // Model-comparison fields (present from the multi-model evaluator).
  /** True when every model that ran returned the same verdict. */
  agreement?: boolean;
  mean_confidence?: number | null;
  min_confidence?: number | null;
  /** How many models ran (1 = single model, no bake-off). */
  model_count?: number;
  total_cost_usd?: string;
  total_cost_zar?: string;
  /** One entry per model that ran; absent or length 1 = no comparison to show. */
  per_model?: AiPerModelResult[];
}

interface DebtorEntity {
  id: string;
  short_id: string;
  /** The debtor's registered legal name. The *write* payload calls this `name`
   *  ({@link CreateDebtorPayload}) — the read and write shapes genuinely differ. */
  legal_name: string;
  /** Empty string, never null, until the registration number is captured. */
  registration_number: string;
  created_at: string;
}

export interface DebtorRelationship {
  id: string;
  short_id: string;
  debtor: DebtorEntity;
  approval_status: ApprovalStatus;
  created_at: string;
}

interface DfStatusEvent {
  from_status: string;
  to_status: string;
  field: string;
  actor_name: string | null;
  note: string | null;
  created_at: string;
}

interface InvoiceStatusEvent {
  from_status: string;
  to_status: string;
  actor_name: string | null;
  note: string | null;
  created_at: string;
}

interface InvoiceDocument {
  id: string;
  short_id: string;
  doc_type: string;
  label: string;
  file_name: string;
  content_type: string;
  created_at: string;
}

interface SubtypeChange {
  from_subtype: DfFacilitySubtype | null;
  to_subtype: DfFacilitySubtype;
  actor_name: string | null;
  cc_ref: string | null;
  note: string | null;
  created_at: string;
}

interface LimitReview {
  previous_limit: string | null;
  new_limit: string;
  actor_name: string | null;
  cc_ref: string | null;
  note: string | null;
  created_at: string;
}

interface DfReviewNote {
  id: string;
  short_id: string;
  body: string;
  author_name: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Invoice shapes
// ---------------------------------------------------------------------------

export interface Invoice {
  id: string;
  short_id: string;
  invoice_number: string;
  invoice_amount: string;
  currency: string;
  invoice_date: string | null;
  due_date: string | null;
  invoice_status: InvoiceStatus;
  debtor_name: string;
  created_at: string;
}

export interface InvoiceDetail extends Invoice {
  documents: InvoiceDocument[];
  status_events: InvoiceStatusEvent[];
}

export interface InvoiceOps extends InvoiceDetail {
  advance_rate: string | null;
  apparent_overdue_flag: boolean;
  apparent_overdue_reason: string;
  funded_amount: string | null;
  // Denormalised enum mirror (APPROVE / DECLINE / MORE_INFO / ""), not the rich object.
  latest_ai_recommendation: string | null;
  funded_at: string | null;
  settled_at: string | null;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Facility shapes
// ---------------------------------------------------------------------------

export interface DfFacility {
  id: string;
  short_id: string;
  /** UUID. For the business's short_id use `entity_short_id` (Ops payloads only). */
  entity: string;
  entity_name: string;
  df_status: DfStatus;
  application_stage: DfApplicationStage;
  facility_subtype: DfFacilitySubtype | null;
  requested_limit: string | null;
  df_intent: string | null;
  trading_history_months: number | null;
  cipc_verified: boolean;
  kyc_verified: boolean;
  sars_verified: boolean;
  bank_account_ownership_verified: boolean;
  // FLOW-125 — what Ops asked for when they bounced this back, in their own
  // words. Empty string when nothing has been requested. Client-facing copy, NOT
  // the internal `DfStatusEvent.note` rendered in the status timeline.
  client_info_request_message: string;
  submitted_at: string | null;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * BRE-200 Screen 6.2 onboarding progress — the client-safe rolled-up figure.
 * N4: percentage + boolean only; the backend never sends the per-condition
 * `unmet`/`waived` lists to a client. Defined here (the lower-level module) and
 * re-exported from kickstart.types to avoid a circular import.
 */
export interface OnboardingProgress {
  complete: boolean;
  percent: number;
}

export interface DfFacilityDetail extends DfFacility {
  documents: DfFacilityDocument[];
  /** Who at CapX is dealing with it; '' when nobody is (Henk, 2026-10-23). */
  dealt_with_by: string;
  status_events: DfStatusEvent[];
  /**
   * FLOW-290 — AI-processing consent state, so the screen can offer the
   * withdrawal it promises. `null` = never asked (not the same as withdrawn).
   */
  ai_consent: AiConsentState | null;

  // BRE-200 Screen 6.2 progress — only on the detail serializer (the facility
  // list stays lean), mirroring the backend (FLOW-64).
  onboarding_progress: OnboardingProgress;
  // BRE-200 DF-OB-06 (FLOW-163) — the client's own signature on the facility
  // agreement. DF-OB-07 (cession) is an OPS confirmation and is deliberately
  // absent from every client shape; it lives on DfFacilityOps only.
  df_agreement_signed: boolean;
  df_agreement_signed_at: string | null;
}

/** One signal in a product-shaped risk lens (Ops-only). */
export interface RiskSummaryField {
  key: string;
  label: string;
  value: string | number | null;
  kind: 'currency' | 'months' | 'count' | 'status' | 'band' | 'percent' | 'score';
  pending?: boolean;
  tone?: 'neutral' | 'warning';
}

export interface RiskSummary {
  lens: 'cash_flow' | 'age_analysis';
  fields: RiskSummaryField[];
}

/** The Kickstart application a DF facility was converted from (KS→DF journey). */
interface KsConversionRef {
  short_id: string;
  ks_status: string;
  amount_requested: string;
}

/**
 * `DfFacilityOpsSerializer` is not the client detail serializer plus extras — it
 * omits three client-shaped fields, so they are subtracted here rather than
 * inherited. Typing a field the payload never carries is worse than not typing
 * it: the compiler then vouches for an `undefined`.
 */
export interface DfFacilityOps extends Omit<
  DfFacilityDetail,
  'documents' | 'onboarding_progress' | 'client_info_request_message' | 'dealt_with_by'
> {
  /** Who at CapX is dealing with it; '' when nobody is. */
  assignee_name: string;
  /**
   * The business's short_id. Ops-only, and distinct from `entity`, which is the
   * UUID — the Ops surfaces address a business by short_id. Mirrors
   * `KsApplicationOps.entity_short_id` (FLOW-160).
   */
  entity_short_id: string;
  // BRE-200 DF-OB-06/07 confirmations with actor (FLOW-163). Ops sees who
  // signed / who confirmed the cession; the client shapes never carry `_by`.
  df_agreement_signed_by: string | null;
  cession_confirmed: boolean;
  cession_confirmed_at: string | null;
  cession_confirmed_by: string | null;
  facility_limit: string | null;
  /** The BUSINESS's `§C7.1` status; null until Ops records one. */
  insurance_status: InsuranceState | null;
  insurance_history: InsuranceHistoryRow[];
  /** The named triggers Ops may pick, by the state they lead to. */
  insurance_triggers: Record<'SUSPENDED' | 'WITHDRAWN', InsuranceTrigger[]>;
  concentration_lock_cause: string | null;
  dri_band: string | null;
  // Denormalised enum mirror (APPROVE / DECLINE / MORE_INFO / ""), not the rich object.
  latest_ai_recommendation: string | null;
  // Full persisted advisory (rationale, confidence, flags) — null when unassessed.
  latest_ai_advisory: LatestAiAdvisory | null;
  converted_from: KsConversionRef | null;
  risk_summary: RiskSummary;
  decided_at: string | null;
  updated_at: string;
  subtype_changes: SubtypeChange[];
  limit_reviews: LimitReview[];
  review_notes: DfReviewNote[];
  /** DOC-52: cross-facility KS recovery flag; null when KS_CLEAR (render nothing). */
  ks_recovery_flag: KsRecoveryFlag | null;
  /**
   * The open declared-account hold (FLOW-277), or `null` when nothing is held.
   *
   * 🚨 **PRE-SIZING (`DOC-58` `UI-DA-58-02`): while this is set, no facility
   * size exists** — render `SizingHoldCardComponent` and no size element.
   */
  sizing_hold: SizingHold | null;
  /**
   * Whether the client has affirmed its account list is COMPLETE (FLOW-284).
   *
   * 🔑 **Read beside `sizing_hold`: it decides whether the release ACTION is
   * offered.** Limb (a) is re-read on every release, so releasing a
   * `DECLARED_ACCOUNTS_NOT_ON_FILE` hold while this is `false` raises an
   * identical hold seconds later — and the API now refuses it with a 409.
   * A fact about the ENTITY, so it is `false` on a file with no hold too.
   */
  affirmation_in_force: boolean;
}

// ---------------------------------------------------------------------------
// Response / payload shapes
// ---------------------------------------------------------------------------

export interface FacilitiesResponse {
  facilities: DfFacility[];
}

export interface DfFacilitySubmitResponse {
  message: string;
  facility: DfFacilityDetail;
}

export interface CreateDfPayload {
  entity: string; // entity short_id
  df_intent?: string;
  requested_limit?: string;
  // Optional KS→DF conversion link: short_id of the originating KS application.
  originating_ks_application?: string;
}

export interface CaptureDfPayload {
  // Applicant-writable capture fields only. The BRE-200 verification gates are
  // set back-office, never by the client — the API rejects them on this endpoint.
  df_intent?: string;
  requested_limit?: string;
  trading_history_months?: number | null;
}

export interface FacilityDecisionPayload {
  /** *Request more information* is withdrawn (FLOW-381, DOC-89). */
  action: 'approve' | 'decline';
  note?: string;
  facility_subtype?: DfFacilitySubtype;
  facility_limit?: string;
  cc_ref?: string;
  reason?: string;
}

export interface CreateDebtorPayload {
  name: string;
  reg_no?: string;
}

export interface CreateInvoicePayload {
  relationship: string; // debtor relationship short_id
  invoice_number: string;
  invoice_amount: string;
  currency?: string;
  invoice_date?: string;
  due_date?: string;
  /** §C5.1 exception path: why an invoice that appears past due is not. */
  apparent_overdue_reason?: string;
}

export type InsuranceState = 'ACTIVE' | 'SUSPENDED' | 'WITHDRAWN';

export interface InsuranceTrigger {
  value: string;
  label: string;
}

/** One row of the entity's insurance audit trail (`§C7.3`), newest first. */
export interface InsuranceHistoryRow {
  from_state: InsuranceState | '';
  to_state: InsuranceState;
  trigger: string;
  reason: string;
  cc_ref: string;
  cgic_reference: string;
  by: string | null;
  created_at: string;
}

export interface InsuranceStatusPayload {
  to_state: InsuranceState;
  reason: string;
  trigger_id?: string;
  cc_ref?: string;
  cgic_reference?: string;
  trigger_resolved?: boolean;
  no_new_adverse_events?: boolean;
}

export interface FundInvoicePayload {
  funded_amount: string;
  note?: string;
  cc_ref?: string;
}

// ---------------------------------------------------------------------------
// Status metadata maps
// ---------------------------------------------------------------------------

export const DF_STATUS_META: Record<DfStatus, StatusMeta> = {
  NONE: { label: 'Not started', variant: 'neutral' },
  IN_PROGRESS: { label: 'In progress', variant: 'warning' },
  ACTIVE: { label: 'Active', variant: 'success' },
};

export const DF_STAGE_META: Record<DfApplicationStage, StatusMeta> = {
  // Amber, not grey: a draft is the one stage that needs the applicant to act
  // (finish + submit), so it must read as "unfinished", not "dormant".
  DRAFT: { label: 'Draft', variant: 'warning' },
  SUBMITTED: { label: 'Submitted', variant: 'default' },
  MORE_INFO_REQUIRED: { label: 'More info required', variant: 'warning' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
};

export const DF_INVOICE_STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  SUBMITTED: { label: 'Submitted', variant: 'default' },
  PENDING_REVIEW: { label: 'Pending review', variant: 'warning' },
  NEEDS_DOCS: { label: 'Needs documents', variant: 'warning' },
  ON_HOLD: { label: 'On hold', variant: 'warning' },
  APPROVED_FOR_FUNDING: { label: 'Approved for funding', variant: 'warning' },
  FUNDED: { label: 'Funded', variant: 'success' },
  OVERDUE: { label: 'Overdue', variant: 'destructive' },
  PARTIALLY_PAID: { label: 'Partially paid', variant: 'warning' },
  SOLD_BACK: { label: 'Sold back', variant: 'neutral' },
  SETTLED: { label: 'Settled', variant: 'success' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
  CANCELLED: { label: 'Cancelled', variant: 'neutral' },
};

export const DF_APPROVAL_STATUS_META: Record<ApprovalStatus, StatusMeta> = {
  PENDING: { label: 'Pending', variant: 'neutral' },
  IN_PROGRESS: { label: 'In progress', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  LIMITED: { label: 'Limited', variant: 'warning' },
  EXCLUDED: { label: 'Excluded', variant: 'destructive' },
  PAUSED: { label: 'Paused', variant: 'neutral' },
  TERMINATED: { label: 'Terminated', variant: 'destructive' },
};

export const DF_SUBTYPE_META: Record<DfFacilitySubtype, StatusMeta> = {
  FACTORING: { label: 'Factoring', variant: 'default' },
  SID: { label: 'SID', variant: 'default' },
  HYBRID: { label: 'Hybrid', variant: 'default' },
};
